import './host'

import { GatewayService } from '@graft/nuget-gc.webportals.gateways'

import { getSessionToken } from '@common/core/helpers'
import { mapWithConcurrency } from '@common/core/utils/mapWithConcurrency'

import { gatewayToObject } from './objects'
import type { Gateway } from './types/gateway.type'
import type { GatewayTimeFilterInput } from './types/gateway-filter.type'
import { GATEWAY_TIME_FILTER_ALL } from './types/gateway-filter.type'
import { invokeRemoveGateway } from './utils/invokeRemoveGateway'
import { resolveGatewayTimeFilter } from './utils/resolveGatewayTimeFilter'

const GATEWAY_OBJECT_MAP_CONCURRENCY = 16

export type { GatewayTimeFilterInput, ResolvedGatewayTimeFilter } from './types/gateway-filter.type'
export { GATEWAY_TIME_FILTER_ALL, GATEWAY_TIME_FILTER_DEFAULT } from './types/gateway-filter.type'

export async function getGateways(projectId: string, filter?: GatewayTimeFilterInput): Promise<Gateway[]> {
  if (!projectId) {
    return []
  }

  try {
    const token = await getSessionToken()
    const { dateFrom, dateTo } = resolveGatewayTimeFilter(filter)
    const graftGateways = await GatewayService.getGateways(token, projectId, dateFrom, dateTo)

    return mapWithConcurrency(graftGateways, (gateway) => gatewayToObject(gateway), GATEWAY_OBJECT_MAP_CONCURRENCY)
  } catch (error) {
    console.error('Failed to get gateways:', error)
    return []
  }
}

export async function getAllGateways(projectId: string): Promise<Gateway[]> {
  return getGateways(projectId, GATEWAY_TIME_FILTER_ALL)
}

export async function addNewGateway(projectId: string, name = ''): Promise<string | null> {
  try {
    const token = await getSessionToken()
    const gatewayId = await GatewayService.addNewGateway(token, projectId, name)
    return gatewayId ?? null
  } catch (error) {
    console.error('Failed to create gateway:', error)
    throw error
  }
}

export async function removeGateway(gatewayId: string): Promise<void> {
  if (!gatewayId) {
    throw new Error('Gateway id is required')
  }

  try {
    await invokeRemoveGateway(gatewayId)
  } catch (error) {
    console.error('Failed to remove gateway:', error)
    throw error
  }
}

const ugmInFlight = new Map<string, Promise<boolean>>()

export function didGatewayReportAnyUgm(gatewayKey: string): Promise<boolean> {
  const inflight = ugmInFlight.get(gatewayKey)
  if (inflight) return inflight

  const promise = (async () => {
    try {
      return await GatewayService.didGatewayReportAnyUgm(gatewayKey)
    } catch (error) {
      console.error('Failed to check if gateway reported any UGM:', error)
      return false
    } finally {
      ugmInFlight.delete(gatewayKey)
    }
  })()

  ugmInFlight.set(gatewayKey, promise)
  return promise
}
